import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Input } from "@/OS/components/ui/input";
import { Label } from "@/OS/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/OS/components/ui/select";
import type { GameModeId, LevelDefinition } from "../levels/types";

export interface HostFormValues {

  serverName: string;
  hostName: string;
  mode: GameModeId;
  mapId: string;
  maxPlayers: number;
}

interface MultiplayerHostProps {
  maps: LevelDefinition[];
  initialValues: HostFormValues;
  isBusy?: boolean;
  error?: string | null;
  onBack: () => void;
  onHost: (values: HostFormValues) => void;
}

function Field({ label, children }: { label: string; children: ReactNode }) {

  return (
    <div className="space-y-2 border-b border-white/10 pb-5">
      <Label className="font-mono text-[11px] uppercase tracking-[0.32em] text-white/70">{label}</Label>
      {children}
    </div>
  );
}

export function MultiplayerHost({ maps, initialValues, isBusy, error, onBack, onHost }: MultiplayerHostProps) {
  const [values, setValues] = useState(initialValues);
  const modeOptions = useMemo(
    () => [
      { value: "dm", label: "Deathmatch" },
      { value: "tdm", label: "Team Deathmatch" },
      { value: "ctf", label: "Capture the Flag" },
    ] as const,
    []
  );

  return (
    <div className="max-w-3xl space-y-8">
      <p className="max-w-2xl text-sm leading-7 text-white/60">Publish a live Cortex server through the relay network and stage a pre-game ready room.</p>

      <div className="grid gap-5 md:grid-cols-2">
        <Field label="Server Name">
          <Input value={values.serverName} onChange={(event) => setValues({ ...values, serverName: event.target.value })} className="border-white/10 bg-black/20 text-white" />
        </Field>
        <Field label="Host Callsign">
          <Input value={values.hostName} onChange={(event) => setValues({ ...values, hostName: event.target.value })} className="border-white/10 bg-black/20 text-white" />
        </Field>
        <Field label="Mode">
          <Select value={values.mode} onValueChange={(mode) => setValues({ ...values, mode: mode as GameModeId })}>
            <SelectTrigger className="border-white/10 bg-black/20 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {modeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Map">
          <Select value={values.mapId} onValueChange={(mapId) => setValues({ ...values, mapId })}>
            <SelectTrigger className="border-white/10 bg-black/20 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {maps.map((map) => (
                <SelectItem key={map.id} value={map.id}>{map.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Max Players">
          <Input type="number" min={2} max={12} value={values.maxPlayers} onChange={(event) => setValues({ ...values, maxPlayers: Number(event.target.value) || 12 })} className="border-white/10 bg-black/20 text-white" />
        </Field>
      </div>

      {error ? <div className="border-b border-red-500/30 pb-4 text-sm text-red-200">{error}</div> : null}

      <div className="flex items-center justify-between gap-4 border-t border-white/10 pt-5">
        <button type="button" className="font-mono text-[11px] uppercase tracking-[0.32em] text-white/60 transition-colors duration-200 hover:text-[#ffb347] focus-visible:outline-none focus-visible:text-[#ffb347]" onClick={onBack}>
          Back
        </button>
        <button type="button" className="font-mono text-[11px] uppercase tracking-[0.34em] text-[#ffb347] transition-opacity duration-200 hover:opacity-80 focus-visible:outline-none" disabled={isBusy} onClick={() => onHost(values)}>
          {isBusy ? "Booting Host" : "Start Hosting"}
        </button>
      </div>
    </div>
  );
}
